import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, Download, Smartphone } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Track visit count
    const visits = parseInt(localStorage.getItem('visit_count') || '0');
    const newVisits = visits + 1;
    localStorage.setItem('visit_count', newVisits.toString());

    // Show prompt after 2 visits
    const dismissed = localStorage.getItem('pwa_prompt_dismissed');
    if (newVisits >= 2 && !dismissed && !window.matchMedia('(display-mode: standalone)').matches) {
      setShowPrompt(true);
    }

    // Listen for beforeinstallprompt event
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installed');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed', 'true');
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed', 'true');
  };

  if (!showPrompt || !deferredPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-md animate-slide-up">
      <Card className="p-4 shadow-xl border-2 border-primary/20">
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-primary" />
            </div>
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold mb-1">
              {t('Апп суулгах', 'Install Lavlah App')}
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              {t(
                'Хурдан нэвтрэх, офлайн ашиглах боломжтой',
                'Quick access, works offline, like a native app'
              )}
            </p>
            
            <div className="flex gap-2">
              <Button onClick={handleInstall} size="sm" className="flex-1">
                <Download className="w-4 h-4 mr-2" />
                {t('Суулгах', 'Install')}
              </Button>
              <Button onClick={handleDismiss} size="sm" variant="ghost" aria-label={t('Хаах', 'Dismiss install prompt')}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
